import React, { useState, useEffect } from 'react';
import { Card, Select, Button, Typography, Spin, Alert, Divider, Table, Space, Tag } from 'antd';
import { ReloadOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { useLocationConfig } from '../hooks/useLocationConfig';
import { useLocationSimulator } from '../hooks/useLocationSimulator';
import { LocationDTO } from '@/shared/api/model';

const { Title, Text } = Typography;
const { Option } = Select;

interface LocationDisplayProps {
  location: LocationDTO | undefined;
  label: string;
  type: string;
}

const LocationDisplay: React.FC<LocationDisplayProps> = ({ location, label, type }) => {
  if (!location) return null;

  return (
    <div className="mb-4">
      <Text strong>{label}:</Text>
      <Tag color="blue" className="ml-2">
        {location.name}
      </Tag>
      <Text className="ml-2" type="secondary">
        (ID: {location.id})
      </Text>
    </div>
  );
};

const LocationSimulator: React.FC = () => {
  const { dark } = useTheme();
  const { configs, fetchConfigsByEntityType, isLoading: isLoadingConfigs } = useLocationConfig();
  const { simulatedLocation, isLoading: isSimulating, simulateLocation } = useLocationSimulator();
  
  const [selectedConfig, setSelectedConfig] = useState<string | undefined>(undefined);
  
  useEffect(() => {
    // Fetch all available configurations
    fetchConfigsByEntityType('ALL');
  }, [fetchConfigsByEntityType]);

  const handleConfigChange = (value: string) => {
    setSelectedConfig(value);
  };

  const handleSimulate = async () => {
    if (selectedConfig) {
      await simulateLocation(selectedConfig);
    }
  };

  const renderLocationDetails = () => {
    if (!simulatedLocation) return null;
    
    return (
      <Card 
        title={<Text strong>Simulated Location Details</Text>}
        className={`mt-4 ${dark ? 'bg-gray-700 text-white' : ''}`}
      >
        <LocationDisplay location={simulatedLocation.zone} label="Zone" type="zone" />
        <LocationDisplay location={simulatedLocation.region} label="Region" type="region" />
        <LocationDisplay location={simulatedLocation.district} label="District" type="district" />
        <LocationDisplay location={simulatedLocation.ward} label="Ward" type="ward" />
        <LocationDisplay location={simulatedLocation.street} label="Street" type="street" />
        
        {simulatedLocation.gps && (
          <div className="mt-4">
            <Text strong>GPS Coordinates:</Text>
            <div className="mt-2">
              <Tag icon={<EnvironmentOutlined />} color="green">
                Lat: {simulatedLocation.gps.latitude.toFixed(6)}
              </Tag>
              <Tag icon={<EnvironmentOutlined />} color="green" className="ml-2">
                Lng: {simulatedLocation.gps.longitude.toFixed(6)}
              </Tag>
            </div>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div>
      <Card
        className={`${dark ? 'bg-gray-800 text-white' : ''}`}
        title={
          <Title level={4} className={`${dark ? 'text-white' : ''} mb-0`}>
            Location Simulator
          </Title>
        }
      >
        <div>
          <div className="mb-4">
            <Text>
              This tool simulates location data based on available location configurations.
              Select a configuration and generate a random valid location that meets the 
              configuration requirements.
            </Text>
          </div>
          
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-1/2">
              <Select
                placeholder="Select a location configuration"
                className="w-full"
                onChange={handleConfigChange}
                loading={isLoadingConfigs}
                disabled={isLoadingConfigs || isSimulating}
              >
                {configs.map(config => (
                  <Option key={config.configName} value={config.configName}>
                    {config.configName} ({config.entityType})
                  </Option>
                ))}
              </Select>
            </div>
            
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleSimulate}
              loading={isSimulating}
              disabled={!selectedConfig || isLoadingConfigs}
            >
              Generate Location
            </Button>
          </div>
          
          {isSimulating ? (
            <div className="text-center py-8">
              <Spin tip="Generating location data..." />
            </div>
          ) : renderLocationDetails()}
        </div>
      </Card>
    </div>
  );
};

export default LocationSimulator;
